import { useState, useEffect, useRef } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-secondary-200/50'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-16">
          {/* Left: Brand */}
          <a
            href="/"
            className="flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <span className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              YOYBUY<span className="text-primary-500">.</span>BOND
            </span>
          </a>

          {/* Center: Category Dropdown */}
          <div className="hidden md:flex items-center" ref={dropdownRef}>
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-secondary-100/80 text-foreground-700 text-sm font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-grid-line w-4 h-4 flex items-center justify-center"></i>
                <span>Категории товаров</span>
                <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-72 bg-background-50 rounded-xl border border-secondary-200/70 overflow-hidden z-50">
                  <div className="max-h-80 overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block px-5 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                        onClick={() => setDropdownOpen(false)}
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full border-2 border-primary-500 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              yoybuy sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              buy it now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-secondary-100 transition-colors cursor-pointer"
          >
            <i className={`w-5 h-5 flex items-center justify-center ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-secondary-200/50">
          <div className="px-4 py-4 space-y-1">
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-2 pb-2">
              Категории
            </p>
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="block px-3 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                {cat.name}
              </a>
            ))}
            <div className="pt-3 border-t border-secondary-200/50 flex flex-col gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block text-center px-5 py-2.5 rounded-full border-2 border-primary-500 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                yoybuy sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block text-center px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                buy it now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}